import React, { useId, useMemo } from 'react';
import { Checkbox } from '../ui/checkbox';
import { cn } from '@/lib/utils';
import { detectPrivacyJurisdiction } from '@/lib/privacyJurisdiction';
import { usePrivacyNotice } from '@/legal/usePrivacyNotice';
import { useI18n } from '@/i18n';

function scrollToPrivacyNotice(onOpen) {
  onOpen?.(true);
  const el = document.getElementById('privacy-notice');
  if (el) {
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
}

function ConsentRow({ id, checked, onCheckedChange, error, requiredMsg, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          className="mt-0.5"
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <label
          htmlFor={id}
          className="text-sm text-slate-700 leading-snug cursor-pointer select-none flex-1 min-w-0"
        >
          {children}
        </label>
      </div>
      {error && (
        <p id={`${id}-error`} className="text-xs text-domio-600 pl-7" role="alert">
          {requiredMsg}
        </p>
      )}
    </div>
  );
}

function PrivacyNoticeLink({ linkText, onOpenNotice }) {
  return (
    <a
      href="#privacy-notice"
      className="text-domio-600 hover:text-domio-700 underline underline-offset-2 font-medium"
      onClick={(e) => {
        e.preventDefault();
        scrollToPrivacyNotice(onOpenNotice);
      }}
    >
      {linkText}
    </a>
  );
}

export function RegistrationPrivacyConsent({
  locale,
  countryCode,
  consent,
  onConsentChange,
  errors = {},
  showSensitiveConsent = false,
  noticeOpen,
  onNoticeOpenChange,
}) {
  const { t } = useI18n();
  const jurisdiction = useMemo(
    () => detectPrivacyJurisdiction({ locale, countryCode }),
    [locale, countryCode],
  );
  const { notice, consent: consentCopy, language } = usePrivacyNotice(jurisdiction);
  const toggleId = useId();
  const panelId = useId();

  const setPrivacy = (v) => onConsentChange({ privacy: v === true });
  const setSensitive = (v) => onConsentChange({ sensitive: v === true });
  const setMarketing = (v) => onConsentChange({ marketing: v === true });

  const requiredMsg = consentCopy.fieldRequired;

  return (
    <div className="space-y-4 pt-2" lang={language}>
      <div className="space-y-3" role="group" aria-labelledby="registration-consent-heading">
        <p id="registration-consent-heading" className="sr-only">
          {t('settings.legal.consentGroupLabel')}
        </p>

        {jurisdiction === 'AU' && (
          <>
            <ConsentRow
              id="consent-privacy-au"
              checked={consent.privacy}
              onCheckedChange={setPrivacy}
              error={errors.privacy}
              requiredMsg={requiredMsg}
            >
              {consentCopy.privacy} (
              <PrivacyNoticeLink
                linkText={consentCopy.privacyLink}
                onOpenNotice={onNoticeOpenChange}
              />
              )
            </ConsentRow>
            {showSensitiveConsent && (
              <ConsentRow
                id="consent-sensitive-au"
                checked={consent.sensitive}
                onCheckedChange={setSensitive}
                error={errors.sensitive}
                requiredMsg={requiredMsg}
              >
                {consentCopy.sensitive}
              </ConsentRow>
            )}
          </>
        )}

        {jurisdiction === 'CO' && (
          <>
            <ConsentRow
              id="consent-privacy-co"
              checked={consent.privacy}
              onCheckedChange={setPrivacy}
              error={errors.privacy}
              requiredMsg={requiredMsg}
            >
              {consentCopy.privacy} (
              <PrivacyNoticeLink
                linkText={consentCopy.privacyLink}
                onOpenNotice={onNoticeOpenChange}
              />
              )
            </ConsentRow>
            <ConsentRow
              id="consent-sensitive-co"
              checked={consent.sensitive}
              onCheckedChange={setSensitive}
              error={errors.sensitive}
              requiredMsg={requiredMsg}
            >
              {consentCopy.sensitive}
            </ConsentRow>
          </>
        )}

        {jurisdiction === 'ES' && (
          <>
            <ConsentRow
              id="consent-privacy-es"
              checked={consent.privacy}
              onCheckedChange={setPrivacy}
              error={errors.privacy}
              requiredMsg={requiredMsg}
            >
              {consentCopy.privacy} (
              <PrivacyNoticeLink
                linkText={consentCopy.privacyLink}
                onOpenNotice={onNoticeOpenChange}
              />
              )
            </ConsentRow>
            <ConsentRow
              id="consent-marketing-es"
              checked={consent.marketing}
              onCheckedChange={setMarketing}
              error={errors.marketing}
              requiredMsg={requiredMsg}
            >
              {consentCopy.marketing}
            </ConsentRow>
            <ConsentRow
              id="consent-sensitive-es"
              checked={consent.sensitive}
              onCheckedChange={setSensitive}
              error={errors.sensitive}
              requiredMsg={requiredMsg}
            >
              {consentCopy.sensitive}
            </ConsentRow>
          </>
        )}
      </div>

      <div className="border-t border-slate-200 pt-4">
        <button
          type="button"
          id={toggleId}
          className="text-sm font-medium text-domio-600 hover:text-domio-700 underline-offset-2 hover:underline"
          aria-expanded={noticeOpen}
          aria-controls={panelId}
          onClick={() => onNoticeOpenChange(!noticeOpen)}
        >
          {noticeOpen ? notice.toggleExpanded : notice.toggleCollapsed}
        </button>

        <div
          id={panelId}
          role="region"
          aria-labelledby={toggleId}
          className={cn('privacy-notice-collapse', noticeOpen && 'privacy-notice-collapse--open')}
        >
          <div>
            <article
              id="privacy-notice"
              className="mt-4 rounded-sm border border-slate-200 bg-white p-4 sm:p-5 text-slate-700 scroll-mt-4 max-h-[min(70vh,28rem)] overflow-y-auto"
            >
              {notice.sections.map((section, idx) => (
                <section key={idx} className={idx > 0 ? 'mt-4' : undefined}>
                  {section.title && (
                    <h3 className="font-manrope text-sm font-semibold text-slate-900 mb-2">
                      {section.title}
                    </h3>
                  )}
                  {section.paragraphs.map((para, pIdx) => (
                    <p key={pIdx} className="text-xs sm:text-sm leading-relaxed mb-2 last:mb-0">
                      {para}
                    </p>
                  ))}
                </section>
              ))}
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}

export { detectPrivacyJurisdiction };
